import React from 'react'
import { Link } from 'react-router-dom'
import SEO from '../components/SEO'
import PartnersSection from '../components/PartnersSection'
import CMSGallery from '../components/CMSGallery'

export default function About() {
  return (
    <div className="space-y-0">
      <SEO
        title="Om Transport4U - Transport4U"
        description="Transport4U er en ung og dynamisk transportvirksomhed i Silkeborg. Gennem mere end 2 år har vi leveret godstransport, temperaturstyret logistik og ekspreslevering til erhvervskunder i hele landet."
      />

      {/* Hero */}
      <section className="bg-navy-950 text-white py-16 lg:py-24 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-sky-950 border border-sky-500/40 text-sky-300 text-xs font-bold uppercase tracking-wider">
              <i className="fa-solid fa-building text-sky-400" aria-hidden="true" />
              <span>Om Virksomheden</span>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight">
              En ung og dynamisk transportpartner
            </h1>
            <p className="text-base sm:text-lg text-slate-300 leading-relaxed">
              Transport4U har gennem mere end 2 år samarbejdet med en lang række toneangivende virksomheder og logistikfirmaer. Vi leverer fleksible, skræddersyede logistikløsninger til alle typer erhvervskunder med fokus på rettidighed og leveringssikkerhed.
            </p>
          </div>
        </div>
      </section>

      {/* Company Story & Strengths */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Left text */}
            <div className="lg:col-span-7 space-y-6">
              <div className="space-y-2">
                <span className="text-xs font-bold uppercase tracking-wider text-sky-600">
                  Vores fundament
                </span>
                <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
                  Præcision, fleksibilitet og ordentlighed
                </h2>
              </div>

              <p className="text-slate-600 text-base leading-relaxed">
                Som transportpartner forstår vi, at en forsinkelse eller temperaturafvigelse kan have store konsekvenser for vores kunders forretning. Derfor har vi investeret målrettet i nye biler af høj kvalitet, avancerede klimaanlæg og erfarne chauffører, der tager personligt ansvar for hver eneste forsendelse.
              </p>

              <p className="text-slate-600 text-base leading-relaxed">
                Med udgangspunkt fra Silkeborg håndterer vi både korte distributionsruter samt lange og komplekse ruter i hele Danmark og det øvrige Norden. Vi dækker alt fra hastepakker på 1 kg til fulde vognlæs på op til 8 europaller samt ikke-standard gods efter forudgående aftale.
              </p>

              {/* Bullet list of core capabilities */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 text-sm text-slate-700 font-medium">
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-check text-sky-600" aria-hidden="true" />
                  <span>Nye biler af høj kvalitet</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-check text-sky-600" aria-hidden="true" />
                  <span>Avancerede klimaanlæg (-20°C til +25°C)</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-check text-sky-600" aria-hidden="true" />
                  <span>Erfarne og ansvarsbevidste chauffører</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-check text-sky-600" aria-hidden="true" />
                  <span>Lange og komplekse ruter</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-check text-sky-600" aria-hidden="true" />
                  <span>Transport fra 1 kg op til 8 europaller</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-check text-sky-600" aria-hidden="true" />
                  <span>Ikke-standard gods efter aftale</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-check text-sky-600" aria-hidden="true" />
                  <span>Fuld godsforsikring</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-check text-sky-600" aria-hidden="true" />
                  <span>Dag-til-dag og dag-til-i-dag kørsel</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-check text-sky-600" aria-hidden="true" />
                  <span>24/7 bestilling alle ugens dage</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-check text-sky-600" aria-hidden="true" />
                  <span>Kørsel i weekender og helligdage</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-check text-sky-600" aria-hidden="true" />
                  <span>Afgang på ruten inden for 2 timer</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-check text-sky-600" aria-hidden="true" />
                  <span>Fokus på rettidighed og leveringssikkerhed</span>
                </div>
              </div>

              <div className="pt-4 flex items-center gap-4">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl bg-sky-600 hover:bg-sky-500 text-white font-bold text-sm transition-colors shadow-xs"
                >
                  <span>Bestil transport online</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </Link>
                <a
                  href="mailto:transport4u.dk@gmail.com"
                  className="inline-flex items-center gap-2 px-5 py-3.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-800 font-bold text-sm transition-colors whitespace-nowrap"
                >
                  <i className="fa-solid fa-envelope text-sky-600" aria-hidden="true" />
                  <span>Send e-mail</span>
                </a>
              </div>
            </div>

            {/* Right location and contact card */}
            <div className="lg:col-span-5 space-y-6">
              <div className="bg-slate-50 rounded-2xl p-8 border border-slate-200/90 shadow-xs space-y-6">
                <h3 className="text-xl font-bold text-slate-900 border-b border-slate-200 pb-4">
                  Virksomhedsoplysninger
                </h3>

                <div className="space-y-4 text-sm">
                  <div className="flex items-start gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-sky-100 text-sky-700">
                      <i className="fa-solid fa-location-dot" aria-hidden="true" />
                    </div>
                    <div>
                      <span className="font-bold text-slate-900 block">Adresse & CVR</span>
                      <span className="text-slate-600">Herningvej 108</span><br />
                      <span className="text-slate-600">8600 Silkeborg</span><br />
                      <span className="text-xs text-slate-500 font-medium">CVR 44899345</span>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-sky-100 text-sky-700">
                      <i className="fa-solid fa-envelope" aria-hidden="true" />
                    </div>
                    <div>
                      <span className="font-bold text-slate-900 block">E-mail</span>
                      <a
                        href="mailto:transport4u.dk@gmail.com"
                        className="text-sky-700 hover:underline font-bold break-all"
                      >
                        transport4u.dk@gmail.com
                      </a>
                      <span className="text-xs text-slate-500 block mt-0.5">Skriv direkte til os</span>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-sky-100 text-sky-700">
                      <i className="fa-solid fa-phone" aria-hidden="true" />
                    </div>
                    <div>
                      <span className="font-bold text-slate-900 block">Telefon (24/7)</span>
                      <a
                        href="tel:+4550352035"
                        className="text-sky-700 hover:underline font-bold whitespace-nowrap"
                      >
                        <span className="whitespace-nowrap">50 35 20 35</span>
                      </a>
                      <span className="text-xs text-slate-500 block mt-0.5">Døgnåben telefonisk modtagelse</span>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-sky-100 text-sky-700">
                      <i className="fa-solid fa-shield-halved" aria-hidden="true" />
                    </div>
                    <div>
                      <span className="font-bold text-slate-900 block">Forsikring & Sikkerhed</span>
                      <span className="text-slate-600">Godsforsikring gældende for samtlige transporter</span>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-sky-100 text-sky-700">
                      <i className="fa-solid fa-calendar-check" aria-hidden="true" />
                    </div>
                    <div>
                      <span className="font-bold text-slate-900 block">Erfaring</span>
                      <span className="text-slate-600">Mere end 2 års dokumenteret erhvervssamarbejde</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Partners Section */}
      <PartnersSection />

      {/* Gallery Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-10 text-center max-w-2xl mx-auto space-y-2">
            <span className="text-xs font-bold uppercase tracking-wider text-sky-600">
              Vores Hverdag
            </span>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
              Køretøjer og drift
            </h2>
            <p className="text-slate-600 text-sm">
              Glimt af vores biler i drift.
            </p>
          </div>

          <CMSGallery gallerySlug="transport4u" maxItems={6} />
        </div>
      </section>
    </div>
  )
}
